import * as _ from 'lodash-es';
import { useState, ChangeEvent, useCallback } from 'react';

import widthIcon from '@/assets/icons/width_icon.svg';
import { kebabCase } from '@/utils';

import { InputRendererProps } from '../inputs';
import { reOrderDuctLengthFields } from '../state/orders';
import { findFieldsByType } from '../store';
import {
  GO,
  TKEvent as EVT,
  CATALOGUE_TYPE,
  CATALOGUE_CATEGORY,
  LENGTH_TYPE as LT,
  NIDSymbolEntity,
  DUCTSHAPES as SP,
  ductFieldsOmmitedBy,
} from '../types';
import {
  setDimensionBy,
  getDimensionBy,
  getSystemBy,
} from '../types/dimension-setting';

export const useDuctLength = (
  lazyCloseHandler: (type?: string | undefined) => void,
) => {
  // ! use cached system - @2025/03/05
  const lastSystem = getSystemBy(LT.D) || '';
  const [systemType, setSystemType] = useState(lastSystem);

  // check dynamic fields for DUCT_LENGTH:
  // @2025/04/25
  const dlFields = findFieldsByType(LT.D);

  // == check last used dimension to determine current values:
  // == this will only take effect in modal openning!
  const lastDimension = getDimensionBy(LT.D);
  const lastShape = lastDimension.shape || SP.ROUND;
  const [shapeType, setShapeType] = useState(lastShape);
  const lastFieldPairs = _.pick(lastDimension, dlFields) as GO;
  const [fieldProps, setFieldProps] = useState(lastFieldPairs);

  /**
   * figure out the attributes displayed on the right side panel
   * @returns `{}`
   */
  const createFeatureEntity = (): NIDSymbolEntity => {
    // ignore unused attributes
    const fieldsOmmited = ductFieldsOmmitedBy(shapeType);
    const pickedProperties: {
      [key: string]: string;
    } = _.omit(fieldProps, fieldsOmmited);
    const toolType = shapeType == SP.FLEX ? EVT.BRKLINE : EVT.NEWLINE;
    const entityType = shapeType == SP.FLEX ? LT.F : LT.D;
    const detail: NIDSymbolEntity = {
      category: CATALOGUE_CATEGORY.D,
      itemType: CATALOGUE_TYPE.L,
      entityType,
      system: systemType,
      icon: widthIcon,
      shape: shapeType,
      toolType,
      ...pickedProperties,
    };

    return detail;
  };

  const applyNewElementHandler = () => {
    const detail = createFeatureEntity();
    // lazy notification(wait for modal close) to map!
    setTimeout(() => {
      // ! different line type, to activate different line tool!
      // @2025/11/25
      const eventName = shapeType == SP.FLEX ? EVT.BRKLINE : EVT.NEWLINE;
      const event = new CustomEvent(eventName, { detail });
      document.dispatchEvent(event);
    }, 200);

    // cache input fields for current duct type
    setDimensionBy(LT.D, {
      ...fieldProps,
      shape: shapeType,
      system: systemType,
    });

    lazyCloseHandler(LT.D);
  };

  const shapeChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    setShapeType(event.target.value);
  };

  // lazy close, and dispatch cancel tool event...
  const cancelNewElementHandler = () => {
    lazyCloseHandler();
    const evt = new Event(EVT.CANCEL_DRAWING_TOOL);
    document.dispatchEvent(evt);
  };

  /**
   * TODO: anyway to separate this function out of this hook? - 2023/07/13
   *
   * figure out the properties needed for each field input component
   *
   * @param fieldName each field name
   * @returns normal object
   */
  const inputFieldRenderer = (fieldName: string): InputRendererProps => {
    const kebabField = kebabCase(fieldName);
    const fieldValueChangeHandler = (key: string, value: string) => {
      // console.log({ key });
      const changedProps = { ...fieldProps, [key]: value };
      setFieldProps(changedProps);
    };
    const inputProps: InputRendererProps = {
      ...fieldProps,
      fieldName, // `DefaultInput` need this
      fieldValue: fieldProps[kebabField], // `DefaultInput` need this
      fieldHandler: fieldValueChangeHandler,
      blurHandler: () => null, // FIXME: placeholder for now - 2023/11/08
    };

    return inputProps;
  };

  /**
   * infer fields to show by `shape`
   */
  const ductFieldsFilterBy = useCallback(
    (shape: string) => {
      // re-ordered fields
      return reOrderDuctLengthFields(dlFields, shape);
    },
    [dlFields],
  );

  return {
    systemType,
    shapeType,
    ductLengthFields: ductFieldsFilterBy(shapeType),
    fieldProps,
    inputFieldRenderer,
    setSystemType,
    applyNewElementHandler,
    shapeChangeHandler,
    cancelNewElementHandler,
  };
};
